import { BALANCE } from '../constants';
import type { Simulation } from '../sim';
import type { Economy, PriceState } from '../types';

function drift(
  price: PriceState,
  rng: { normal(mean: number, deviation: number): number },
  thetaPerWeek: number,
  sigmaPerWeek: number,
  clamp: readonly [number, number],
): void {
  const theta = thetaPerWeek / 7;
  const sigma = sigmaPerWeek / Math.sqrt(7);
  const next = price.price + theta * (price.mean - price.price) + rng.normal(0, sigma);
  price.price = Math.round(Math.max(clamp[0], Math.min(clamp[1], next)));
}

interface CommoditySpec {
  key: keyof Economy['market'];
  meanCents: number;
  clamp: readonly [number, number];
  thetaPerWeek: number;
  sigmaPerWeek: number;
}

/** Every commodity the market drifts, in order. The order is load-bearing:
 * each entry consumes a normal() draw from the shared market fork daily, so
 * inserting or reordering reshuffles every price path after that point (and
 * re-baselines the balance harness). Append only. */
export const COMMODITIES: readonly CommoditySpec[] = [
  {
    key: 'iron',
    meanCents: BALANCE.ironPriceMeanCents,
    clamp: BALANCE.ironPriceClamp,
    thetaPerWeek: BALANCE.ironThetaPerWeek,
    sigmaPerWeek: BALANCE.ironSigmaPerWeek,
  },
  {
    key: 'gold',
    meanCents: BALANCE.goldPriceMeanCents,
    clamp: BALANCE.goldPriceClamp,
    thetaPerWeek: BALANCE.goldThetaPerWeek,
    sigmaPerWeek: BALANCE.goldSigmaPerWeek,
  },
  { key: 'corn', ...BALANCE.commodityMarket.corn },
  { key: 'wheat', ...BALANCE.commodityMarket.wheat },
  { key: 'cattle', ...BALANCE.commodityMarket.cattle },
  { key: 'glass', ...BALANCE.commodityMarket.glass },
  { key: 'chips', ...BALANCE.commodityMarket.chips },
  { key: 'power', ...BALANCE.commodityMarket.power },
];

/** Daily mean-reverting price drift across every commodity. */
export function stepMarketDaily(sim: Simulation): void {
  const market = sim.state.economy.market;
  const rng = sim.forks.market;
  for (const spec of COMMODITIES) {
    drift(market[spec.key], rng, spec.thetaPerWeek, spec.sigmaPerWeek, spec.clamp);
  }
}

/** Everything a boom or bust can hit — trucked goods and ores, not power. */
const EVENT_POOL = ['iron', 'gold', 'corn', 'wheat', 'cattle', 'glass', 'chips'] as const;

const EVENT_LABELS: Record<(typeof EVENT_POOL)[number], string> = {
  iron: 'Iron',
  gold: 'Gold',
  corn: 'Corn',
  wheat: 'Wheat',
  cattle: 'Cattle',
  glass: 'Glass',
  chips: 'Semiconductor',
};

/** Weekly boom/bust roll (generalizes the old gold spike): one commodity
 * jumps or craters, the drift mean-reverts it home over the event weeks. */
export function stepMarketWeekly(sim: Simulation): void {
  const economy = sim.state.economy;
  const rng = sim.forks.market;
  const active = economy.marketEvent;
  if (active) {
    active.weeksLeft -= 1;
    if (active.weeksLeft <= 0) economy.marketEvent = null;
    return;
  }
  if (!rng.chance(BALANCE.marketEventChancePerWeek)) return;
  const good = EVENT_POOL[rng.integer(0, EVENT_POOL.length - 1)]!;
  const boom = rng.chance(0.5);
  const spec = COMMODITIES.find((candidate) => candidate.key === good)!;
  const price = economy.market[good];
  const multiplier = boom ? BALANCE.marketEventBoomMultiplier : BALANCE.marketEventBustMultiplier;
  price.price = Math.round(Math.max(spec.clamp[0], Math.min(spec.clamp[1], price.price * multiplier)));
  economy.marketEvent = { good, kind: boom ? 'boom' : 'bust', weeksLeft: BALANCE.marketEventWeeks };
  sim.pushEvent({ kind: 'marketEvent', good, boom });
  sim.pushEvent({
    kind: 'toast',
    message: boom
      ? `${EVENT_LABELS[good]} fever! Buyers are paying a premium.`
      : `${EVENT_LABELS[good]} prices cratered. Ride it out.`,
  });
}
